<template>
  <div class="columns">
    <div class="column"></div>
    <div class="column is-narrow">
      <label
        class="checkbox"
        v-if="worker.repository_url"
      >
        <input
          type="checkbox"
          v-model="allVersions"
        />
        Display all versions
      </label>
      <ImportModal
        :worker-id="worker.id"
        class="is-pulled-right ml-3"
        v-on:created="fetchVersions"
      />
    </div>
  </div>

  <div
    v-if="error"
    class="notification is-warning"
  >
    {{ error }}
  </div>
  <template v-else>
    <Paginator
      :response="versionsPage"
      :loading="loading"
      v-slot="{ results }"
      v-model:page="page"
      singular="version"
      plural="versions"
    >
      <table class="table is-fullwidth is-hoverable">
        <thead>
          <tr>
            <th>Id</th>
            <th>Version</th>
            <th>Tag</th>
            <th>Branch</th>
            <th>State</th>
            <th>Docker image</th>
            <th>Created</th>
            <th v-if="selectable || haveRevisions(results)">Actions</th>
          </tr>
        </thead>
        <tbody>
          <Row
            :selectable="selectable"
            :process-id="processId"
            :local="local"
            :key="version.id"
            :version="version"
            v-for="version in results"
            v-on:selected-version="$emit('selected-version', $event)"
          />
        </tbody>
      </table>
    </Paginator>
  </template>
</template>

<script lang="ts">
import { mapActions } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { WorkerVersionListParameters } from '@/api'
import Paginator from '@/components/Paginator.vue'
import { errorParser } from '@/helpers'
import { useNotificationStore, useWorkerStore } from '@/stores'
import type { PageNumberPagination, UUID } from '@/types'
import type { Worker, WorkerVersion } from '@/types/worker'

import ImportModal from './ImportModal.vue'
import Row from './Row.vue'

export default defineComponent({
  components: {
    ImportModal,
    Paginator,
    Row,
  },
  emits: {
    'selected-version': (value: WorkerVersion) =>
      typeof value === 'object' && value !== null && typeof value.id === 'string',
  },
  props: {
    // Lists versions related to a specific worker
    worker: {
      type: Object as PropType<Worker>,
      required: true,
    },
    /**
     * Whether or not buttons are shown to allow selecting a worker version.
     * This will trigger `selected-version` events.
     *
     * Without `processId` or `local` set, any worker version can be selected.
     */
    selectable: {
      type: Boolean,
      default: false,
    },
    /**
     * If `selectable` is set, selecting a version will both cause an event
     * and create a WorkerRun on the specified process.
     *
     * Only available versions will be selectable.
     */
    processId: {
      type: String as PropType<UUID | null>,
      default: null,
    },
    /**
     * If true, only worker versions that do not have a revision attached
     * can be selected (for creating local worker runs).
     *
     * This has no effect if `selectable` is not set.
     */
    local: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    error: null as string | null,
    versionsPage: undefined as PageNumberPagination<WorkerVersion> | undefined,
    loading: false,
    recommendedVersionLoading: false,
    page: 1,
    /**
     * If set to false, only display versions with a tag or from the master/main branch
     */
    allVersions: false,
  }),
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    ...mapActions(useWorkerStore, ['listVersions']),
    async fetchVersions() {
      this.versionsPage = undefined
      this.error = null
      this.loading = true
      let mode = 'complete' as WorkerVersionListParameters['mode']
      if (this.worker.repository_url) mode = this.allVersions ? 'complete' : 'simple'
      const payload: WorkerVersionListParameters = {
        page: this.page,
        mode,
      }
      try {
        this.versionsPage = await this.listVersions(this.worker.id, payload)
      } catch (err) {
        this.error = errorParser(err)
      } finally {
        this.loading = false
      }
    },
    haveRevisions(results: WorkerVersion[]) {
      return results.some((workerVersion) => workerVersion.revision_url !== null)
    },
  },
  watch: {
    page: 'fetchVersions',
    worker: {
      immediate: true,
      handler(newValue, oldValue) {
        if (oldValue?.id === newValue.id) return
        this.page = 1
        this.fetchVersions()
      },
    },
    allVersions: {
      handler() {
        this.page = 1
        this.fetchVersions()
      },
    },
  },
})
</script>
